import { DialogComponent } from '@theme/dialog';

/**
 * localStorage is unavailable in private windows and when site data is blocked,
 * so every access is wrapped. Without it the popup simply falls back to showing
 * once per page load.
 */
const store = {
  /**
   * @param {string} key
   * @returns {string | null}
   */
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  /**
   * @param {string} key
   * @param {string} value
   */
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Ignored on purpose — see above.
    }
  },
};

const DAY = 24 * 60 * 60 * 1000;

/**
 * A newsletter dialog that opens itself on a timer, on scroll depth or on exit
 * intent, and swaps to a reward pane once the visitor has signed up.
 *
 * Extends the theme's DialogComponent so scroll locking, the open/close
 * animations, click-outside and Escape all keep working the way they do in
 * every other modal.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} [copyButton] - The copy-the-code button.
 *
 * @extends DialogComponent<Refs>
 */
export class NewsletterPopupComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:close', this.#handleClose);

    // The customer form posts and reloads the page. Liquid has already rendered
    // whichever pane the submission produced, so the outcome is just read off
    // the markup — and a reopen here has to bypass the usual trigger and
    // frequency rules.
    const state = this.querySelector('[data-state]')?.getAttribute('data-state');

    if (state === 'success') {
      store.set(this.#key('subscribed'), '1');
      this.showDialog();
      return;
    }

    // A rejected email reloads the page too — reopen so the error is not lost.
    if (state === 'error') {
      this.showDialog();
      return;
    }

    // In the theme editor the merchant opens the popup by selecting the section,
    // otherwise it would fire on a timer over whatever they are editing.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
      return;
    }

    if (this.#shouldShow()) this.#arm();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#disarm();
    this.removeEventListener('dialog:close', this.#handleClose);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
  }

  /**
   * Copies the discount code to the clipboard and flips the button label.
   */
  copyCode = async () => {
    const { copyButton } = this.refs;
    const code = this.dataset.discountCode;

    if (!code || !(copyButton instanceof HTMLElement)) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // The Clipboard API needs a secure context and is blocked in some
      // browsers — fall back to a throwaway textarea and execCommand.
      const field = document.createElement('textarea');

      field.value = code;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      this.append(field);
      field.select();

      try {
        document.execCommand('copy');
      } catch {
        return;
      } finally {
        field.remove();
      }
    }

    copyButton.setAttribute('data-copied', '');
    setTimeout(() => copyButton.removeAttribute('data-copied'), 2500);
  };

  /** @returns {'delay' | 'scroll' | 'exit'} */
  get trigger() {
    const trigger = this.dataset.trigger;

    return trigger === 'scroll' || trigger === 'exit' ? trigger : 'delay';
  }

  /** @returns {number} Milliseconds to wait before opening. */
  get delay() {
    return Number(this.dataset.delay ?? 0) * 1000;
  }

  /** @returns {number} How far down the page to scroll before opening, 0-1. */
  get scrollDepth() {
    return Number(this.dataset.scrollPercent ?? 0) / 100;
  }

  /** @returns {number} Days to stay hidden after the visitor dismisses it. */
  get frequencyDays() {
    return Number(this.dataset.frequencyDays ?? 0);
  }

  /** @returns {boolean} */
  get showOnMobile() {
    return this.dataset.showOnMobile !== 'false';
  }

  /**
   * @param {string} suffix
   * @returns {string}
   */
  #key(suffix) {
    return `${this.dataset.storageKey ?? 'newsletter-popup'}:${suffix}`;
  }

  /**
   * @returns {boolean} Whether this visitor is due to see the popup.
   */
  #shouldShow() {
    if (!this.showOnMobile && window.matchMedia('(max-width: 749px)').matches) return false;
    if (store.get(this.#key('subscribed'))) return false;

    const dismissedAt = Number(store.get(this.#key('dismissed')));

    if (!dismissedAt) return true;

    return Date.now() - dismissedAt > this.frequencyDays * DAY;
  }

  /**
   * Starts listening for whichever trigger the merchant chose.
   */
  #arm() {
    switch (this.trigger) {
      case 'exit':
        // Exit intent is a mouse signal, so touch devices get the delay instead.
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          document.addEventListener('mouseout', this.#handleMouseOut);
          return;
        }

        this.#timer = setTimeout(this.#open, this.delay);
        return;

      case 'scroll':
        // A page too short to scroll would never reach the threshold.
        if (document.documentElement.scrollHeight > window.innerHeight) {
          window.addEventListener('scroll', this.#handleScroll, { passive: true });
          return;
        }

        this.#timer = setTimeout(this.#open, this.delay);
        return;

      default:
        this.#timer = setTimeout(this.#open, this.delay);
    }
  }

  #disarm() {
    clearTimeout(this.#timer);
    document.removeEventListener('mouseout', this.#handleMouseOut);
    window.removeEventListener('scroll', this.#handleScroll);
  }

  #open = () => {
    // The cart drawer, quick add and search are dialogs too. Never stack on top
    // of one — no dismissal is recorded, so a later page view tries again.
    const otherDialogOpen = [...document.querySelectorAll('dialog[open]')].some(
      (dialog) => dialog !== this.refs.dialog
    );

    if (otherDialogOpen) return;

    this.#disarm();
    this.showDialog();
  };

  #handleClose = () => {
    if (window.Shopify?.designMode) return;

    store.set(this.#key('dismissed'), String(Date.now()));
  };

  /** @param {MouseEvent} event */
  #handleMouseOut = (event) => {
    // relatedTarget is null only when the pointer leaves the viewport itself.
    if (event.relatedTarget || event.clientY > 0) return;

    this.#open();
  };

  #handleScroll = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;

    if (scrollable <= 0) return;
    if (window.scrollY / scrollable < this.scrollDepth) return;

    this.#open();
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.contains(this)) return;

    this.showDialog();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.contains(this)) return;

    this.closeDialog();
  };
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopupComponent);
